import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { 
  Calendar, 
  Users, 
  FileText, 
  BarChart3, 
  Edit, 
  Mail, 
  Phone, 
  UserCircle, 
  Building, 
  FileCheck,
  UserCheck,
  UserPlus
} from "lucide-react";
import { PartnerInviteButton } from "./PartnerInviteButton";
import { useContacts } from "@/hooks/use-contacts";
import { useActivitiesByPartner } from "@/hooks/use-activities";
import { usePartnerDeals } from "@/hooks/use-partner-deals";
import { usePartnerOwnedContactDeals } from "@/hooks/use-partner-owned-contact-deals";
import { usePartnerCommunications } from "@/hooks/use-partner-communications";
import { usePartnerPayments } from "@/hooks/use-partner-payments";
import { ActivitiesTab } from "./ActivitiesTab";
import { ContactDealsList } from "./ContactDealsList";
import { StatsTab } from "./StatsTab";

interface PartnerDetailModalProps {
  partner: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: () => void;
}

export const PartnerDetailModal = ({
  partner,
  open,
  onOpenChange,
  onEdit
}: PartnerDetailModalProps) => {
  const {
    data: contacts = []
  } = useContacts();
  const {
    data: activities = [],
    isLoading: activitiesLoading
  } = useActivitiesByPartner(partner.id);
  const {
    data: directPartnerDeals = [],
    isLoading: directDealsLoading
  } = usePartnerDeals(partner.id);
  const {
    data: ownedContactDeals = [],
    isLoading: ownedDealsLoading
  } = usePartnerOwnedContactDeals(partner.id);
  const {
    data: communications = []
  } = usePartnerCommunications(partner.id);
  const {
    data: payments = []
  } = usePartnerPayments(partner.id);

  // Combine direct deals and deals from owned contacts
  const allPartnerDeals = [...directPartnerDeals, ...ownedContactDeals];
  const dealsLoading = directDealsLoading || ownedDealsLoading;

  // Filter contacts that are assigned to this partner
  const partnerContacts = contacts.filter(contact => contact.external_owner_id === partner.id);
  
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Signed':
        return 'bg-green-100 text-green-800';
      case 'Not-Signed':
        return 'bg-red-100 text-red-800';
      case 'Informal':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getPartnerTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'Affiliate':
        return 'bg-blue-100 text-blue-800';
      case 'Associate':
        return 'bg-purple-100 text-purple-800';
      case 'Reseller':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] overflow-hidden flex flex-col [&>button]:text-slate-700 [&>button]:hover:text-slate-900">
        <DialogHeader className="bg-white -mx-6 -mt-6 px-6 py-4 rounded-t-xl border-b border-slate-200">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <UserCircle className="h-5 w-5" />
              Partner Details
            </DialogTitle>
            {onEdit && (
              <Button onClick={onEdit} variant="outline" size="sm" className="border-slate-300 text-slate-700 hover:bg-slate-100 my-0 mx-[32px]">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </DialogHeader>
        
        <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="contacts">
              <Users className="h-4 w-4 mr-2" />
              Contacts ({partnerContacts.length})
            </TabsTrigger>
            <TabsTrigger value="deals">
              <FileText className="h-4 w-4 mr-2" />
              Deals ({allPartnerDeals.length})
            </TabsTrigger>
            <TabsTrigger value="stats">
              <BarChart3 className="h-4 w-4 mr-2" />
              Stats
            </TabsTrigger>
            <TabsTrigger value="activities">
              <Calendar className="h-4 w-4 mr-2" />
              Activities ({activities.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="flex-1 overflow-auto">
            <div className="space-y-6 p-6">
              {/* Partner Header */}
              <div className="flex items-start gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={partner.profile_image} alt={`${partner.first_name} ${partner.last_name}`} />
                  <AvatarFallback className="bg-teal-100 text-teal-600 font-semibold text-lg">
                    {getInitials(partner.first_name, partner.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-slate-800">
                    {partner.first_name} {partner.last_name}
                  </h2>
                  <div className="flex gap-2 mt-2">
                    <Badge className={getPartnerTypeBadgeColor(partner.partner_type)}>
                      {partner.partner_type}
                    </Badge>
                    <Badge className={getStatusBadgeColor(partner.status)}>
                      {partner.status}
                    </Badge>
                    <Badge className={partner.agreement_signed ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                      <FileCheck className="h-3 w-3 mr-1" />
                      {partner.agreement_signed ? 'Agreement Signed' : 'Agreement Pending'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Contact Information</h3>
                  
                  <div className="flex items-center gap-3 text-slate-600">
                    <Mail className="h-5 w-5" />
                    <span>{partner.email}</span>
                  </div>
                  
                  {partner.phone && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <Phone className="h-5 w-5" />
                      <span>{partner.phone}</span>
                    </div>
                  )}

                  {/* User Account Status */}
                  <div className="space-y-3 pt-3 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700">Account Access & Invitation</h4>
                    {partner.user_id ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <UserCheck className="h-4 w-4 text-green-600" />
                          <span className="text-green-600 font-medium text-sm">Active User Account</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          User already has account access as they were selected from existing users
                        </div>
                        {partner.invitation_accepted_at && (
                          <div className="text-xs text-slate-500">
                            Invitation accepted: {new Date(partner.invitation_accepted_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-slate-600">
                          <UserPlus className="h-4 w-4" />
                          <span className="text-sm">No account access - invitation required</span>
                        </div>
                        <div className="w-full">
                          <PartnerInviteButton partner={partner} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Partnership Details</h3>
                  
                  <div className="space-y-2">
                    <div className="text-sm text-slate-500">Partner Type</div>
                    <div className="font-medium">{partner.partner_type}</div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="text-sm text-slate-500">Status</div>
                    <div className="font-medium">{partner.status}</div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="text-sm text-slate-500">Agreement Status</div>
                    <div className="font-medium">
                      {partner.agreement_signed ? 'Signed' : 'Pending Signature'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {partner.notes && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Notes</h3>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-slate-700 whitespace-pre-wrap">{partner.notes}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                <div className="space-y-2">
                  <div className="text-sm text-slate-500">Created</div>
                  <div className="text-sm font-medium">
                    {new Date(partner.created_at).toLocaleDateString()} at {new Date(partner.created_at).toLocaleTimeString()}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-slate-500">Last Updated</div>
                  <div className="text-sm font-medium">
                    {new Date(partner.updated_at).toLocaleDateString()} at {new Date(partner.updated_at).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="contacts" className="flex-1 overflow-auto">
            <div className="space-y-4 p-6">
              <h3 className="text-lg font-semibold">Contacts assigned to {partner.first_name} {partner.last_name}</h3>
              {partnerContacts.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600">No contacts assigned to this partner.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {partnerContacts.map(contact => (
                    <div key={contact.id} className="p-4 border rounded-lg bg-white shadow-sm">
                      <div className="flex items-start gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={contact.profile_image} />
                          <AvatarFallback className="bg-slate-100 text-slate-600">
                            {contact.first_name?.charAt(0)}{contact.last_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800">
                            {contact.first_name} {contact.last_name}
                          </div>
                          <div className="text-sm text-slate-600 truncate">{contact.email}</div>
                          {contact.job_title && (
                            <div className="text-sm text-slate-500">{contact.job_title}</div>
                          )}
                          {contact.organizations && (
                            <div className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                              <Building className="h-3 w-3" />
                              {contact.organizations.name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="deals" className="flex-1 overflow-auto">
            <ContactDealsList deals={allPartnerDeals} isLoading={dealsLoading} title="Partner Deals" showContact={true} showAssignee={false} />
          </TabsContent>

          <TabsContent value="stats" className="flex-1 overflow-auto">
            <StatsTab deals={allPartnerDeals} isLoading={dealsLoading} title={`${partner.first_name} ${partner.last_name} Statistics`} />
          </TabsContent>

          <TabsContent value="activities" className="flex-1 overflow-auto">
            <ActivitiesTab activities={activities} isLoading={activitiesLoading} partnerId={partner.id} title="Partner Activities" />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};